import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { CheckCircle, FileText, Loader2, Euro, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Quote = () => {
  const { t } = useLanguage();
  const { quoteId } = useParams();
  const navigate = useNavigate();
  
  const [quote, setQuote] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const quoteRes = await axios.get(`${API}/quotes/${quoteId}`);
        setQuote(quoteRes.data);
        
        if (quoteRes.data.client_id) {
          const clientRes = await axios.get(`${API}/clients/${quoteRes.data.client_id}`);
          setClient(clientRes.data);
        }
      } catch (error) {
        console.error('Error fetching quote:', error);
        toast.error(t('error'));
      }
      setLoading(false);
    };
    fetchData();
  }, [quoteId, t]);

  const handleAcceptQuote = async () => {
    setAccepting(true);
    try {
      await axios.put(`${API}/quotes/${quoteId}/accept`);
      
      // Generate contract
      const contractData = {
        client_id: quote.client_id,
        quote_id: quoteId,
        content: generateContractContent(),
        terms: [
          'Paiement à 30 jours date de facture',
          'Engagement de 12 mois minimum',
          'Support technique inclus',
          'Mises à jour gratuites pendant la durée du contrat'
        ]
      };
      
      const contractRes = await axios.post(`${API}/contracts`, contractData);
      toast.success('Devis accepté ! Contrat généré.');
      navigate(`/contract/${contractRes.data.id}`);
    } catch (error) {
      console.error('Error accepting quote:', error);
      toast.error(t('error'));
    }
    setAccepting(false);
  };

  const generateContractContent = () => {
    return `
CONTRAT DE PRESTATION DE SERVICES IA

Entre les soussignés :

TANNOU (SASU en cours de constitution)
Ci-après dénommée "Le Prestataire"

ET

${client?.name || 'Le Client'}
Email: ${client?.email || ''}
Ci-après dénommé "Le Client"

ARTICLE 1 - OBJET
Le présent contrat a pour objet de définir les conditions dans lesquelles le Prestataire fournira au Client des services d'intelligence artificielle.

ARTICLE 2 - PRESTATIONS
Le Prestataire s'engage à fournir les services suivants :
${quote?.items?.map(item => `- ${item.description}`).join('\n') || ''}

ARTICLE 3 - DURÉE
Le présent contrat est conclu pour une durée de 12 mois à compter de sa signature.

ARTICLE 4 - PRIX
Le montant total des prestations s'élève à ${quote?.total?.toFixed(2) || '0.00'} € HT.

ARTICLE 5 - PAIEMENT
Le paiement sera effectué dans un délai de 30 jours suivant la date de facturation.

ARTICLE 6 - CONFIDENTIALITÉ
Les parties s'engagent à maintenir la confidentialité de toutes les informations échangées.

Fait à Paris, le ${new Date().toLocaleDateString('fr-FR')}

En deux exemplaires originaux.
    `;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <p className="text-slate-500">Devis introuvable</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-50" data-testid="quote-page">
      <div className="max-w-3xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-heading font-bold text-3xl text-slate-900 mb-2">
            Devis #{quoteId.substring(0, 8).toUpperCase()}
          </h1>
          <p className="text-slate-600">
            Créé le {new Date(quote.created_at).toLocaleDateString('fr-FR')}
          </p>
        </div>

        {/* Quote Card */}
        <Card className="shadow-lg border-0 mb-8" data-testid="quote-card">
          <CardHeader className="border-b">
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Devis pour {client?.name || 'Client'}</CardTitle>
                <p className="text-sm text-slate-500 mt-1">{client?.email}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                quote.status === 'accepted' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-amber-100 text-amber-700'
              }`}>
                {quote.status === 'accepted' ? 'Accepté' : 'En attente'}
              </span>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {/* Items Table */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-center">Qté</TableHead>
                  <TableHead className="text-right">Prix unitaire</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quote.items?.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{item.description}</TableCell>
                    <TableCell className="text-center">{item.quantity}</TableCell>
                    <TableCell className="text-right">{item.unit_price.toFixed(2)} €</TableCell>
                    <TableCell className="text-right">{(item.quantity * item.unit_price).toFixed(2)} €</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Total */}
            <div className="mt-6 pt-6 border-t flex justify-between items-center">
              <span className="text-lg font-semibold">Total HT</span>
              <div className="flex items-center gap-2 text-2xl font-bold text-primary">
                <Euro className="w-6 h-6" />
                {quote.total?.toFixed(2)}
              </div>
            </div>

            {/* Notes */}
            {quote.notes && (
              <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">{quote.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        {quote.status !== 'accepted' && (
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleAcceptQuote}
              disabled={accepting}
              className="bg-primary hover:bg-primary-hover text-white rounded-full px-8 py-6 gap-2"
              data-testid="accept-quote-btn"
            >
              {accepting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  {t('acceptQuote')}
                </>
              )}
            </Button>
          </div>
        )}

        {quote.status === 'accepted' && client?.contract_id && (
          <div className="text-center">
            <Button
              onClick={() => navigate(`/contract/${client.contract_id}`)}
              className="bg-primary hover:bg-primary-hover text-white rounded-full px-8 py-6 gap-2"
              data-testid="view-contract-btn"
            >
              {t('viewContract')}
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
